import React from "react";
import { isEmpty, pick, keys, get } from "lodash";
import ItemForm from "./ItemForm";
import {
  compose,
  withHandlers,
  setDisplayName,
  lifecycle,
  branch,
  withProps,
  renderComponent,
  renderNothing
} from "recompose";
import { connect } from "react-redux";
import {
  actions,
  getConfirmedItems,
  getRequiredItems,
  getCompletedItems,
  getRecommendedItems
} from "../modules/items";
import Button from "./CustomButton";
import { guid } from "../helpers/index";
import ItemList from "./ItemList";
import {
  withFetchContractActions,
  withState as withContractSte
} from "../containers/WithFetchContract/index";
import { withRouter } from "react-router-dom";
import FontAwesomeIcon from "@fortawesome/react-fontawesome";
import { faTrash, faPencilAlt } from "@fortawesome/fontawesome-free-solid";
import Checkbox from "./CustomCheckbox";

let formInputs = null;

const mapStateToProps = state => ({
  submittingItem: state.items.submittingItem,
  failSubmitItem: state.items.failSubmitItem,
  successSubmitItem: state.items.successSubmitItem,
  currentDealer: state.dealers.currentDealer,
  currentContract: state.contracts.currentContract,
  currentItem: state.items.currentItem,
  items: state.items.items,
  confirmedItems: getConfirmedItems(state),
  requiredItems: getRequiredItems(state),
  completedItems: getCompletedItems(state),
  recommendedItems: getRecommendedItems(state),
  role: state.settings.role,
  showItems: state.items.showItems
});

const mapDispatchToProps = {
  createItemAsync: actions.createItemAsync,
  createItemSync: actions.createItemSync,
  updateItemSync: actions.updateItemSync,
  updateItemAsync: actions.updateItemAsync,
  setCurrentItem: actions.setCurrentItem,
  deleteItemSync: actions.deleteItemSync,
  deleteItemAsync: actions.deleteItemAsync,
  resetState: actions.resetState,
  setShowItems: actions.setShowItems
};

const withItemState = connect(
  mapStateToProps,
  mapDispatchToProps
);

const withHandleSubmit = compose(
  withContractSte,
  withRouter,
  withFetchContractActions,
  withHandlers({
    onSubmit: ({
      setShowItems,
      fetchContract,
      createItemAsync,
      createItemSync,
      updateItemAsync,
      updateItemSync,
      currentContract,
      currentItem
    }) => (data, { resetForm }) => {
      const clearForm = () => {
        resetForm({
          labor_time: "",
          part_price: "",
          reason: "",
          type_service: "for_hours",
          taxable: 0
        });
        setTimeout(() => {
          const laborTimeInput = get(formInputs, "labor_time", null);
          if (laborTimeInput) {
            laborTimeInput.focus();
          }
        }, 200);
      };
      if (currentItem) {
        if (currentContract) {
          updateItemAsync({
            data,
            dealer_id: currentContract.dealer.id,
            contract_id: currentContract.id,
            item_id: currentItem.id
          }).then(clearForm);
        } else {
          updateItemSync(data);
          clearForm();
        }
      } else {
        if (currentContract) {
          createItemAsync({
            data,
            dealer_id: currentContract.dealer.id,
            contract_id: currentContract.id
          }).then(resolvedItem => {
            const properState = ["waiting_for_service", "in_service"].includes(
              currentContract.aasm_state
            );
            if (properState) {
              fetchContract();
              setShowItems("all");
            }
            clearForm();
          });
        } else {
          createItemSync(guid(), data);
          clearForm();
        }
      }
    },
    clearItem: ({ setCurrentItem }) => ev => {
      setCurrentItem(null);
    }
  })
);

const withActionHandlers = withHandlers({
  onSetShowItems: ({ setShowItems }) => type => ev => {
    setShowItems(type);
  }
});

const isCustomer = ({ role }) => role === "customer";

const nonOptimalStates = states =>
  compose(...states.map(state => branch(state.when, state.render)));

const withShowItems = withProps(
  ({
    items,
    requiredItems,
    confirmedItems,
    completedItems,
    recommendedItems,
    showItems
  }) => {
    const displayedItems = {
      all: items,
      required: requiredItems,
      confirmed: confirmedItems,
      completed: completedItems,
      recommended: recommendedItems
    };
    return { items: displayedItems[showItems] };
  }
);

const withAllChecked = compose(
  withProps(({ items }) => ({
    allItemsChecked: key =>
      isEmpty(items) ? false : items.reduce((is, item) => is && item[key], true)
  })),
  withHandlers({
    onCheckAllItems: ({
      allItemsChecked,
      items,
      updateItemAsync,
      currentContract
    }) => key => ev => {
      const checkedAll = allItemsChecked(key);
      Promise.all(
        items.map(item =>
          updateItemAsync({
            dealer_id: currentContract.dealer.id,
            contract_id: currentContract.id,
            item_id: item.id,
            data: Object.assign({}, item, { [key]: !checkedAll })
          })
        )
      );
    }
  })
);

const withDiagnosticProps = compose(
  withProps(({ contract, currentContract }) => ({
    contract: contract || currentContract
  })),
  withProps(({ contract, role }) => ({
    onPostDiagnostic:
      contract &&
      !(
        ["in_diagnostic", "approve_diagnostic"].includes(contract.aasm_state) &&
        role === "staff"
      ),
    onPreDiagnostic:
      contract &&
      [
        "in_diagnostic",
        "approve_diagnostic",
        "customer_confirmation_needed"
      ].includes(contract.aasm_state),
    onService:
      contract &&
      ["in_service"].includes(contract.aasm_state) &&
      role === "staff",
    onCustomerConfirmation:
      contract &&
      ["customer_confirmation_needed"].includes(contract.aasm_state),
    isPayed:
      contract &&
      ["waiting_for_pickup", "completed"].includes(contract.aasm_state)
  }))
);

const ItemsEnhacer = compose(
  setDisplayName("Items"),
  withItemState,
  withShowItems,
  withAllChecked,
  branch(({ items }) => isEmpty(items), renderNothing),
  withDiagnosticProps,
  withHandlers({
    onClickUpdate: ({ setCurrentItem }) => item => () => {
      setCurrentItem(item);
    },
    onClickDelete: ({
      currentContract,
      deleteItemAsync,
      deleteItemSync
    }) => item => () => {
      if (window.confirm("Do you really want to remove this service?")) {
        if (currentContract) {
          deleteItemAsync({
            dealer_id: currentContract.dealer.id,
            contract_id: currentContract.id,
            item_id: item.id
          });
        } else {
          deleteItemSync(item.id);
        }
      }
    },
    onConfirmItem: ({ updateItemAsync, contract }) => ({
      type,
      item
    }) => ev => {
      updateItemAsync({
        dealer_id: contract.dealer.id,
        contract_id: contract.id,
        item_id: item.id,
        data: Object.assign({}, item, { [type]: !item[type] })
      });
    }
  })
);

const EnhancedList = ItemsEnhacer(
  ({
    items,
    submittingItem,
    onClickDelete,
    onClickUpdate,
    onCustomerConfirmation,
    onPreDiagnostic,
    onPostDiagnostic,
    onConfirmItem,
    onService,
    isPayed,
    allItemsChecked,
    onCheckAllItems,
    role,
    showItems
  }) => (
    <div>
      <ItemList items={items || []}>
        {({ item }) => {
          const colClass = "col-auto px-2";
          return (
            <div>
              <div className="row align-items-center justify-content-lg-end ">
                {role === "staff" && !isPayed && (
                  <div className={colClass}>
                    <Button
                      edit
                      size="sm"
                      disabled={submittingItem}
                      onClick={onClickUpdate(item)}
                    >
                      <FontAwesomeIcon icon={faPencilAlt} />
                    </Button>
                    <Button
                      edit
                      size="sm"
                      disabled={submittingItem}
                      onClick={onClickDelete(item)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </div>
                )}

                {onPreDiagnostic && (
                  <div className={colClass}>
                    <Checkbox
                      label="Required"
                      number={`item-required-${item.id}`}
                      checked={item.required}
                      onChange={onConfirmItem({ type: "required", item })}
                      disabled={onPostDiagnostic}
                    />
                  </div>
                )}
                {onCustomerConfirmation && (
                  <div className={colClass}>
                    <Checkbox
                      label="Confirm"
                      number={`item-confirm-${item.id}`}
                      checked={item.confirmed}
                      onChange={onConfirmItem({ type: "confirmed", item })}
                    />
                  </div>
                )}
                {onService && ["confirmed", "completed"].includes(showItems) && (
                  <div className={colClass}>
                    <Checkbox
                      number={`item-completed-${item.id}`}
                      checked={item.completed}
                      label="Complete"
                      onChange={onConfirmItem({ type: "completed", item })}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        }}
      </ItemList>
      <div className="col justify-content-lg-end serviceFooter">
        {onPreDiagnostic && (
          <div className="col-auto">
            <Checkbox
              number="require-all"
              label="Require All"
              checked={allItemsChecked("required")}
              onChange={onCheckAllItems("required")}
              disabled={onPostDiagnostic}
            />
          </div>
        )}
        {onCustomerConfirmation && (
          <div className="col-auto">
            <Checkbox
              number="confirm-all"
              label="Confirm All"
              checked={allItemsChecked("confirmed")}
              onChange={onCheckAllItems("confirmed")}
            />
          </div>
        )}
        {onService && ["confirmed", "completed"].includes(showItems) && (
          <div className="col-auto">
            <Checkbox
              number={"complete-all"}
              label="Complete All"
              checked={allItemsChecked("completed")}
              onChange={onCheckAllItems("completed")}
            />
          </div>
        )}
      </div>
    </div>
  )
);

export const withRolesDisplay = nonOptimalStates([
  { when: isCustomer, render: renderComponent(EnhancedList) }
]);

const withFormProps = withProps(({ currentItem }) => ({
  initialValues: itemInitialValues(currentItem)
}));

const EnhancedForm = compose(
  withItemState,
  branch(
    ({ currentContract, role }) =>
      !(
        !currentContract ||
        (![
          "pending_diagnostic",
          "waiting_for_payment",
          "waiting_for_invoice",
          "waiting_for_pickup",
          "completed"
        ].includes(currentContract.aasm_state) && role) === "staff"
      ),
    renderNothing
  ),
  withHandleSubmit,
  withFormProps
)(ItemForm);

const withLifeCycle = lifecycle({
  componentDidMount() {
    const { currentContract, setShowItems } = this.props;
    if (
      currentContract &&
      ["waiting_for_service", "in_service",
        "waiting_for_payment"].includes(currentContract.aasm_state)
    ) {
      setShowItems("confirmed");
    }
    if (
      currentContract &&
      [
       // "waiting_for_invoice",
        "waiting_for_pickup",
        "completed"
      ].includes(currentContract.aasm_state)
    ) {
      setShowItems("completed");
    }
  },
  componentWillUnmount() {
    this.props.resetState();
  }
});

const ItemsTab = compose(
  setDisplayName("ItemsTab"),
  withItemState,
  branch(
    ({ currentContract, role }) =>
      !(
        currentContract &&
        (["waiting_for_service", "in_service"].includes(
          currentContract.aasm_state
        ) && role) === "staff"
      ),
    renderNothing
  ),
  withActionHandlers
)(({ onSetShowItems, showItems }) => {
  const colClass = "col-md-3 col-sm-6 col-xs-12 mb-3";
  return (
    <div className="row justify-content-between">
      <div className={colClass + " pr-md-1"}>
        <Button
          block
          color="info"
          onClick={onSetShowItems("all")}
          simple={showItems !== "all"}
        >
          All Items
        </Button>
      </div>
      <div className={colClass + " px-md-1"}>
        <Button
          block
          color="info"
          onClick={onSetShowItems("required")}
          simple={showItems !== "required"}
        >
          Required Items
        </Button>
      </div>
      <div className={colClass + " px-md-1"}>
        <Button
          block
          color="info"
          onClick={onSetShowItems("confirmed")}
          simple={showItems !== "confirmed"}
        >
          Confirmed Items
        </Button>
      </div>
      <div className={colClass + " pl-md-1"}>
        <Button
          block
          color="info"
          onClick={onSetShowItems("completed")}
          simple={showItems !== "completed"}
        >
          Completed Items
        </Button>
      </div>
    </div>
  );
});

const enhanced = compose(
  setDisplayName("ItemSection"),
  withItemState,
  withProps(({ currentDealer, currentContract, values }) => ({
    currentContract: currentContract
      ? currentContract
      : Object.assign({}, values, { dealer: currentDealer })
  })),
  withLifeCycle
);

const ItemSection = enhanced(({ title }) => (
  <div>
    {title && (
      <h4>
        <b>{title}</b>
      </h4>
    )}
    <ItemsTab />
    <EnhancedForm>
      {({ inputs }) => {
        formInputs = inputs;
      }}
    </EnhancedForm>
    <EnhancedList />
  </div>
));

const itemInitialValues = currentItem => {
  let initialValues = {
    labor_time: "",
    part_price: "",
    reason: "",
    type_service: "for_hours",
    flat_fee: "",
    taxable: 0
  };
  return isEmpty(currentItem)
    ? initialValues
    : pick(currentItem, keys(initialValues));
};

export default ItemSection;
